import { FC, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box, Button, List, ListItemButton, ListItemIcon, ListItemText, Paper, TextField, Typography, alpha,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { UserProfile } from "../security/userProfileService";

type Props = {
  profiles: UserProfile[];
  selected: string | null;
  onSelect: (profileId: string) => void;
  // Profile stored on the account; used to warn that navigating away drops an unsaved selection.
  savedProfileId?: string | null;
  error?: string;
};

export const ProfilePicker: FC<Props> = ({ profiles, selected, onSelect, savedProfileId, error }) => {
  const location = useLocation();
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLDivElement>(null);
  const hasScrolled = useRef(false);

  const selectedProfile = profiles.find((p) => p.id === selected);
  const filtered = profiles.filter((p) => (p.name ?? "").toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  const hasUnsavedSelection = !!selected && savedProfileId !== undefined && selected !== savedProfileId;

  // Bring the selected profile into view inside the list once, without scrolling the page.
  useEffect(() => {
    const list = listRef.current;
    const row = selectedRowRef.current;
    if (hasScrolled.current || !list || !row) return;
    list.scrollTop = row.offsetTop - list.clientHeight / 2 + row.clientHeight / 2;
    hasScrolled.current = true;
  }, [selectedProfile]);

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {selectedProfile ? (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderWidth: 2,
            borderColor: "primary.main",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <ManageAccountsIcon color="primary" fontSize="large" />
          <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">Valgt profil</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{selectedProfile.name}</Typography>
            {hasUnsavedSelection && (
              <Typography variant="caption" color="warning.main">Lagre kontoen for å beholde profilvalget</Typography>
            )}
          </Box>
          <Button
            component={Link}
            to={{ pathname: `/edit-profile/${selectedProfile.id}`, state: { from: location.pathname } }}
            size="small"
            variant="contained"
            startIcon={<EditIcon />}
          >
            Rediger profil
          </Button>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderStyle: "dashed", borderColor: error ? "error.main" : "divider", display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <ManageAccountsIcon color="disabled" fontSize="large" />
          <Box>
            <Typography color="text.secondary">Ingen profil valgt – velg fra lista under</Typography>
            {error && <Typography variant="body2" color="error">{error}</Typography>}
          </Box>
        </Paper>
      )}

      <TextField
        label="Søk i profiler"
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Paper ref={listRef} variant="outlined" sx={{ maxHeight: 280, overflowY: "auto", position: "relative" }}>
        <List dense disablePadding>
          {filtered.map((p) => {
            const isSelected = p.id === selected;
            return (
              <ListItemButton
                key={p.id}
                ref={isSelected ? selectedRowRef : undefined}
                selected={isSelected}
                onClick={() => onSelect(p.id)}
                sx={{
                  "&.Mui-selected": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) },
                  "&.Mui-selected:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18) },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isSelected && <CheckCircleIcon color="primary" fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={p.name}
                  primaryTypographyProps={{ fontWeight: isSelected ? 600 : 400, color: isSelected ? "primary" : undefined }}
                />
              </ListItemButton>
            );
          })}
          {!filtered.length && (
            <ListItemText sx={{ px: 2, py: 1 }} secondary="Ingen profiler funnet" />
          )}
        </List>
      </Paper>
    </Box>
  );
};
